import React, { useEffect, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { Box, Stack } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAudioPlaybackState } from '../../hooks/useAudioPlaybackState';
import { useAudioStream } from '../../hooks/useAudioStream';
import { useAudioEventListener } from '../../hooks/useAudioEventListener';
import { AudioTimeIndicator } from '../controls/AudioTimeIndicator';

import { MiniplayerControls } from './MiniplayerControls';

type MiniplayerProps = {
	showTimeIndicator?: boolean;
};

export const Miniplayer = ({ showTimeIndicator }: MiniplayerProps): JSX.Element => {
	const intl = useIntl();
	const [
		{ activeContent, playbackSpeed, stream, isPlaying, isLoading },
		{ onContentPaused, onContentResumed, onContentEnded, onContentError },
	] = useAudioPlaybackState();
	const containerRef = useRef<HTMLDivElement>(null);
	const { trackAIResultAction, trackAIResultView } = useAIEventsInstrumentation();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { audioRef } = useAudioStream(stream, onContentError);
	useAudioEventListener({ audioRef, onContentPaused, onContentResumed, onContentEnded });

	useEffect(() => {
		trackAIResultView();

		const container = containerRef.current;

		if (container) {
			container.focus(); // Bring focus upon opening miniplayer
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const sendAnalyticsUIEvent = useCallback(
		(actionSubjectId: string, additionalAttributes?: Record<string, number | string>) => {
			const attributes = {
				audioType: activeContent?.sourceType,
				summaryLocale: intl.locale,
				...additionalAttributes,
			};
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'miniplayer',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					attributes,
				},
			}).fire();
			trackAIResultAction(`${actionSubjectId}Clicked`, { attributes });
		},
		[activeContent?.sourceType, createAnalyticsEvent, intl.locale, trackAIResultAction],
	);

	useEffect(() => {
		const audio = audioRef.current;
		if (audio) {
			sendAnalyticsUIEvent('audioPlayBackSpeed', { playbackSpeed });
			audio.playbackRate = playbackSpeed;
		}
	}, [playbackSpeed, sendAnalyticsUIEvent, audioRef]);

	return (
		// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
		<Box paddingBlock="space.050" tabIndex={0} ref={containerRef}>
			<Box>
				{/* eslint-disable-next-line jsx-a11y/media-has-caption -- The summary is already available in text so the track isn't very necessary */}
				<audio data-testid="miniplayer-audio" ref={audioRef} />
				<Stack space="space.150">
					<MiniplayerControls audioRef={audioRef} isPlaying={isPlaying} isLoading={isLoading} />
					{showTimeIndicator && <AudioTimeIndicator audioRef={audioRef} isLoading={isLoading} />}
				</Stack>
			</Box>
		</Box>
	);
};
